import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import { BrowserRouter as Router, Routes, Route, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import FemtyzHome from "./pages/FemtyzHome";
import SettingsUI from "./pages/SettingsUI";
import SearchResults from "./pages/SearchResults";
import ProductDetail from "./pages/ProductDetail";
import ProductDetailSingle from "./pages/ProductDetailSingle";
import Subscription from "./pages/Subscription";
import ContactUsV2 from "./pages/ContactUsV2";
import TermsAndConditions from "./pages/TermsAndConditions";
import FAQ from "./pages/FAQ";
import About from "./pages/About";
import PlatformProducts from "./pages/PlatformProducts";
import FeaturesProducts from "./pages/FeaturesProducts";
import TopDealsProducts from "./pages/TopDealsProducts";
import AuthOverlay from "./components/auth/AuthOverlay";
import ForgotPasswordOverlay from "./components/auth/ForgotPasswordOverlay";
import MobileMenuDrawer from "./components/MobileMenuDrawer";
import { logout } from "./store/auth/authSlice";
import { ToastProvider } from "./components/ToastProvider";
import { fetchPlans } from "./store/plans/plansSlice";
import { useToast } from "./components/ToastProvider";

function AppShell() {
  const [auth, setAuth] = useState({ open: false, mode: "login" });
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const authStatus = useSelector((state) => state.auth.status);
  const authError = useSelector((state) => state.auth.error);
  const isLoggedIn = Boolean(token);
  const { showToast } = useToast();
  const prevTokenRef = useRef(token);
  const prevAuthErrorRef = useRef(authError);

  function handleOpenAuth(mode) {
    setAuth({ open: true, mode: mode || "login" });
  }

  useEffect(() => {
    const prevToken = prevTokenRef.current;
    if (prevToken && !token) {
      showToast({ type: "info", title: "Logged out", message: "You have been logged out." });
    }
    prevTokenRef.current = token;
  }, [token, showToast]);

  useEffect(() => {
    if (authStatus !== "failed") return;
    if (!authError) return;
    if (prevAuthErrorRef.current === authError) return;
    prevAuthErrorRef.current = authError;
    showToast({ type: "error", title: "Auth failed", message: authError });
  }, [authStatus, authError, showToast]);

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            <FemtyzHome
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/settings"
          element={
            <SettingsUI
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/search"
          element={
            <SearchResults
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/product/:id"
          element={
            <ProductDetail
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/product-store/:platform/:id"
          element={
            <ProductDetailSingle
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/subscription"
          element={
            <Subscription
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/billing/success"
          element={
            <Subscription
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/billing/cancel"
          element={
            <Subscription
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/about"
          element={
            <About
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/contact"
          element={
            <ContactUsV2
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/terms"
          element={
            <TermsAndConditions
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/faq"
          element={
            <FAQ
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/platform/:platform"
          element={
            <PlatformProducts
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/features"
          element={
            <FeaturesProducts
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/top-deals"
          element={
            <TopDealsProducts
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
      </Routes>

      <AuthOverlay
        open={auth.open}
        initialMode={auth.mode}
        onClose={() => setAuth((s) => ({ ...s, open: false }))}
        onLoginSuccess={() => {
          setAuth((s) => ({ ...s, open: false }));
          dispatch(fetchPlans());
        }}
        onOpenForgotPassword={() => {
          setAuth((s) => ({ ...s, open: false }));
          setForgotPasswordOpen(true);
        }}
      />
      <ForgotPasswordOverlay
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
        onOpenLogin={() => {
          setForgotPasswordOpen(false);
          handleOpenAuth("login");
        }}
      />
      <MobileMenuDrawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onOpenAuth={handleOpenAuth}
        isLoggedIn={isLoggedIn}
        onLogout={() => {
          dispatch(logout());
          setMenuOpen(false);
          navigate("/");
        }}
        onSelectMenuItem={(key) => {
          if (key === "home") navigate("/");
          if (key === "about") navigate("/about");
          if (key === "profile") {
            navigate("/settings");
          }
          if (key === "subscription") navigate("/subscription");
          if (key === "contact") navigate("/contact");
          if (key === "terms & conditions") navigate("/terms");
          if (key === "faq") navigate("/faq");
        }}
      />
    </>
  );
}

function App() {
  return (
    <ToastProvider>
      <Router>
        <AppShell />
      </Router>
    </ToastProvider>
  );
}

export default App;
